import { Component, useEffect, useRef, useState } from 'react'
import type { ComponentType, CSSProperties, ErrorInfo, HTMLAttributes, ReactNode, Ref } from 'react'
import { isKnownWidget, resolveCar } from '@racedash/shared'
import type { DashboardLayout, LayoutEntry, Rect, WidgetKind } from '@racedash/shared'
import { DeadTile } from './Tile'
import { widgetViews } from './widgets/registry'
import type { WidgetProps } from './widgets/props'
import type { DashboardData } from './useSessionData'
import type { ScreenContext } from './screenContext'
import './dashboard.css'

/**
 * Render a layout.
 *
 * No layout library. Grid coordinates map straight onto CSS Grid, so the thing
 * that runs unattended on a pit wall for twenty-five hours is a few dozen lines
 * of markup.
 *
 * The pieces below are exported because the editor is built from exactly these
 * — same surface, same cell maths, same widget rendering — so what you arrange
 * is literally what runs, rather than a preview kept in agreement with it.
 */
export default function DashboardGrid({
  layout,
  data,
  primaryCar,
  now,
  context,
}: {
  layout: DashboardLayout
  data: DashboardData
  primaryCar: string | null
  now: Date
  context: ScreenContext
}) {
  const { ref } = useCellMetrics(layout.rows, layout.columns)

  if (layout.widgets.length === 0) {
    return (
      <div className="dash-empty">
        <p>This dashboard has no widgets yet. Choose "Edit layout" to add some.</p>
      </div>
    )
  }

  return (
    <div className="dash-stage">
      <GridSurface layout={layout} surfaceRef={ref}>
        {layout.widgets.map((entry) => (
          <GridCell key={entry.id} rect={entry}>
            <WidgetTile
              entry={entry}
              data={data}
              primaryCar={primaryCar}
              now={now}
              context={context}
            />
          </GridCell>
        ))}
      </GridSurface>

      <DataErrors errors={data.errors} />
    </div>
  )
}

/** Listener failures, said out loud rather than folded into empty tiles. */
export function DataErrors({ errors }: { errors: string[] }) {
  if (errors.length === 0) return null
  return (
    <div className="dash-errors" role="status">
      {/* A refused query looks exactly like a session with no data, and that
          has already cost an afternoon once. */}
      Some timing data could not be read: {errors.join('; ')}
    </div>
  )
}

/** The grid itself: the cell template and nothing else. */
export function GridSurface({
  layout,
  surfaceRef,
  children,
}: {
  layout: DashboardLayout
  surfaceRef: Ref<HTMLDivElement>
  children: ReactNode
}) {
  return (
    <div
      className="dash"
      ref={surfaceRef}
      style={{
        // minmax(0, 1fr) rather than 1fr. A bare 1fr is minmax(auto, 1fr), so
        // any tile taller than its share — a lap list, most obviously — grows
        // its row and pushes the dashboard off the bottom of the screen. The
        // whole "it never scrolls" property rests on this.
        gridTemplateColumns: `repeat(${layout.columns}, minmax(0, 1fr))`,
        gridTemplateRows: `repeat(${layout.rows}, minmax(0, 1fr))`,
      }}
    >
      {children}
    </div>
  )
}

/** One placed cell of the grid. Knows nothing about what goes in it. */
export function GridCell({
  rect,
  style,
  children,
  ...rest
}: { rect: Rect; style?: CSSProperties; children?: ReactNode } & HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      style={{
        gridColumn: `${rect.x + 1} / span ${rect.w}`,
        gridRow: `${rect.y + 1} / span ${rect.h}`,
        minWidth: 0,
        minHeight: 0,
        ...style,
      }}
      {...rest}
    >
      {children}
    </div>
  )
}

/** One widget, inside its own failure boundary. */
export function WidgetTile({
  entry,
  data,
  primaryCar,
  now,
  context,
}: {
  entry: LayoutEntry
  data: DashboardData
  primaryCar: string | null
  now: Date
  context: ScreenContext
}) {
  return (
    <WidgetBoundary entry={entry}>
      <Widget entry={entry} data={data} primaryCar={primaryCar} now={now} context={context} />
    </WidgetBoundary>
  )
}

function Widget({
  entry,
  data,
  primaryCar,
  now,
  context,
}: {
  entry: LayoutEntry
  data: DashboardData
  primaryCar: string | null
  now: Date
  context: ScreenContext
}) {
  if (!isKnownWidget(entry)) {
    return <DeadTile reason={`Unknown widget "${entry.unknownKind}"`} />
  }

  const kind = entry.config.kind as WidgetKind
  // The cast is the price of looking a component up by a runtime value: the
  // registry's mapped type has already guaranteed that this component takes
  // this kind's config, but the lookup itself erases that.
  const View = widgetViews[kind] as ComponentType<WidgetProps<WidgetKind>>

  return (
    <View
      config={entry.config}
      car={resolveCar(entry.car, primaryCar)}
      data={data}
      now={now}
      context={context}
    />
  )
}

/**
 * Keep one widget's failure to one tile.
 *
 * A dashboard is looked at during a race. A widget that throws on some shape of
 * data nobody anticipated must cost its own tile and nothing else — the
 * alternative is a blank screen at the moment the screen matters most.
 */
class WidgetBoundary extends Component<
  { entry: LayoutEntry; children: ReactNode },
  { failed: boolean }
> {
  state = { failed: false }

  static getDerivedStateFromError() {
    return { failed: true }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('widget failed', this.props.entry.id, error, info.componentStack)
  }

  render() {
    if (this.state.failed) return <DeadTile reason="This widget stopped working" />
    return this.props.children
  }
}

export interface CellMetrics {
  /** One cell, in pixels. */
  w: number
  h: number
  /** The gap between cells, which a drag has to cross as well. */
  gap: number
}

/**
 * Measure the grid: publish the cell height as a CSS variable, and hand back
 * the numbers the editor needs to turn pointer movement into cells.
 *
 * The cell height is what the whole type scale hangs off. Measured rather than
 * computed from viewport units because the dashboard does not always own the
 * entire viewport — there is a header above it, and an editor rail beside it —
 * and a scale derived from 100dvh would be wrong by exactly the height of
 * whatever else is on screen.
 */
export function useCellMetrics(rows: number, columns: number) {
  const ref = useRef<HTMLDivElement | null>(null)
  const [metrics, setMetrics] = useState<CellMetrics>({ w: 0, h: 0, gap: 0 })

  useEffect(() => {
    const el = ref.current
    if (!el) return

    const apply = () => {
      const style = getComputedStyle(el)
      const gap = parseFloat(style.rowGap) || 0
      const padY = (parseFloat(style.paddingTop) || 0) + (parseFloat(style.paddingBottom) || 0)
      const padX = (parseFloat(style.paddingLeft) || 0) + (parseFloat(style.paddingRight) || 0)

      const h = Math.max(24, (el.clientHeight - padY - gap * (rows - 1)) / Math.max(1, rows))
      const w = Math.max(8, (el.clientWidth - padX - gap * (columns - 1)) / Math.max(1, columns))

      el.style.setProperty('--cell-h', `${h}px`)
      // Replaced only when the numbers actually move. A ResizeObserver fires
      // more often than that, and setting state each time would re-render the
      // editor in the middle of a drag.
      setMetrics((prev) =>
        Math.abs(prev.h - h) < 0.5 && Math.abs(prev.w - w) < 0.5 && prev.gap === gap
          ? prev
          : { w, h, gap },
      )
    }

    apply()
    const observer = new ResizeObserver(apply)
    observer.observe(el)
    return () => observer.disconnect()
  }, [rows, columns])

  return { ref, metrics }
}
